import React, { createContext, useState, useContext, useEffect } from "react"
import axios from "axios"
import { auth } from "../firebase"
import {
  onAuthStateChanged,
  signOut,
  signInWithPhoneNumber,
  RecaptchaVerifier
} from "firebase/auth"

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true) // Start loading until Firebase confirms state
  const [error, setError] = useState(null)

  // Listen for Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const token = await firebaseUser.getIdToken()
        localStorage.setItem("token", token)
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`

        // We only care about their email/phone for the frontend UI right now
        // For SMS logins, Firebase doesn't inherently have the email, so we pull it from what they just typed
        const sessionEmail = firebaseUser.email || localStorage.getItem("directory_user_email")

        setUser({
          email: sessionEmail,
          phone: firebaseUser.phoneNumber,
          uid: firebaseUser.uid
        })
      } else {
        // User is signed out
        setUser(null)
        localStorage.removeItem("token")
        delete axios.defaults.headers.common["Authorization"]
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Automatically check tokens and refresh them if needed via Firebase
  useEffect(() => {
    const interval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken(true) // force refresh
          localStorage.setItem("token", token)
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
        } catch (e) {
          console.error("Error refreshing token", e)
        }
      }
    }, 10 * 60 * 1000) // Refresh every 10 mins proactively just in case
    return () => clearInterval(interval)
  }, [])

  // Response interceptor to handle token refresh and eligibility check failures globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Prevent infinite loops and handle 401 Unauthorized (Expired ID Token)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          
          if (auth.currentUser) {
            try {
              console.log("🔄 [AuthContext] 401 error. Attempting to refresh token...")
              const token = await auth.currentUser.getIdToken(true) // force refresh
              localStorage.setItem("token", token)
              axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
              originalRequest.headers["Authorization"] = `Bearer ${token}`
              return axios(originalRequest)
            } catch (refreshErr) {
              console.error("❌ [AuthContext] Token refresh failed:", refreshErr)
              await logout()
              return Promise.reject(error)
            }
          } else {
            console.warn("⚠️ [AuthContext] 401 error and no current user. Logging out.")
            await logout()
            return Promise.reject(error)
          }
        }

        // Handle 403 Forbidden (Eligibility revoked)
        if (error.response?.status === 403) {
          const serverError = error.response?.data?.error
          console.warn("⚠️ [AuthContext] 403 Forbidden error caught:", serverError)
          setError(serverError || "Your account is no longer authorized to access the directory.")
          await logout()
          return Promise.reject(error)
        }

        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, [])

  const checkEligibility = async (email) => {
    setLoading(true)
    setError(null)
    try {
      const response = await axios.post("/.netlify/functions/login", { email })
      localStorage.setItem("directory_user_email", email)
      return response.data // { message, phone }
    } catch (err) {
      setError(err.response?.data?.error || "Email not authorized to access the directory")
      throw err
    } finally {
      setLoading(false)
    }
  }

  const sendLoginLink = async (email) => {
    setLoading(true)
    setError(null)
    try {
      // Use the new custom Netlify function to send branded OTP codes via WordPress
      const response = await axios.post('/.netlify/functions/send-login-email', { 
        email, 
        origin: window.location.origin 
      })
      
      // Store the validation token so the login component can use it to verify the code
      return response.data.validationToken
    } catch (err) {
      console.error("Error sending login OTP:", err)
      setError(err.response?.data?.error || "Failed to send login code. Please try again.")
      throw err
    } finally {
      setLoading(false)
    }
  }

  const verifyEmailOtp = async (email, code, validationToken) => {
    setLoading(true)
    setError(null)
    try {
      const response = await axios.post("/.netlify/functions/verify-email-otp", {
        email,
        code,
        validationToken
      })

      const { customToken } = response.data
      
      // Sign in to Firebase using the Custom Token generated by our backend
      const { signInWithCustomToken } = await import("firebase/auth")
      const userCredential = await signInWithCustomToken(auth, customToken)
      
      // Proactively set the Authorization header immediately so subsequent 
      // fetches (like the Directory) have it ready right away.
      const idToken = await userCredential.user.getIdToken()
      axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`
      localStorage.setItem("token", idToken)
      
      return true
    } catch (err) {
      console.error("Error verifying code:", err)
      const msg = err.response?.data?.error || "Invalid login code."
      setError(msg)
      throw new Error(msg)
    } finally {
      setLoading(false)
    }
  }

  const setupRecaptcha = (buttonId) => {
    // If a verifier already exists, we must clear it first.
    // This prevents the "reCAPTCHA client element has been removed" error 
    // which happens when React remounts the component.
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear()
      } catch (e) {
        console.warn("Error clearing reCAPTCHA:", e)
      }
      window.recaptchaVerifier = null
    }

    window.recaptchaVerifier = new RecaptchaVerifier(auth, buttonId, {
      'size': 'invisible',
      'callback': (response) => {
        // reCAPTCHA solved
      }
    });
    
    return window.recaptchaVerifier;
  }

  const sendSmsCode = async (phoneNumber, appVerifier) => {
    setLoading(true)
    setError(null)
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier)
      window.confirmationResult = confirmationResult;
      return confirmationResult;
    } catch (err) {
      setError("Failed to send SMS code.")
      throw err
    } finally {
      setLoading(false)
    }
  }

  const verifySmsCode = async (code) => {
    setLoading(true)
    setError(null)
    try {
      await window.confirmationResult.confirm(code)
    } catch (err) {
      setError("Invalid SMS code.")
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    await signOut(auth)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        checkEligibility,
        sendLoginLink,
        verifyEmailOtp,
        setupRecaptcha,
        sendSmsCode,
        verifySmsCode,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export default AuthContext
